import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  deleteAssistantConversation,
  fetchAssistantConversations,
  fetchAssistantMessages,
  setAssistantFeedback,
} from '@/api/assistant/assistantApi';
import type {
  AssistantConversation,
  AssistantFeedback,
  AssistantFeedbackReason,
  AssistantMessage,
  AssistantMessagePage,
} from '@/api/assistant/assistantApi.types';
import type { PaginatedResult } from '@/api/pagination.types';
import { ASSISTANT_CONVERSATION_PAGE_SIZE } from '@/constants/assistant';
import {
  ASSISTANT_CONVERSATIONS_QUERY_KEY,
  ASSISTANT_MESSAGES_QUERY_KEY,
  buildAssistantConversationsQueryKey,
  buildAssistantMessagesQueryKey,
} from '@/constants/queryKeys';

export function useAssistantConversationsQuery(
  page: number,
  search: string,
  isEnabled: boolean
): UseQueryResult<PaginatedResult<AssistantConversation>> {
  return useQuery({
    queryKey: buildAssistantConversationsQueryKey(page, search),
    queryFn: function loadConversations() {
      return fetchAssistantConversations(page, ASSISTANT_CONVERSATION_PAGE_SIZE, search);
    },
    placeholderData: keepPreviousData,
    enabled: isEnabled,
  });
}

function readOlderPageCursor(page: AssistantMessagePage): number | undefined {
  const oldestMessage = page.items[0];
  if (page.hasMore === false || oldestMessage === undefined) {
    return undefined;
  }
  return oldestMessage.sequence;
}

export function useAssistantMessagesQuery(
  conversationId: string | null
): UseInfiniteQueryResult<InfiniteData<AssistantMessagePage, number | null>> {
  return useInfiniteQuery({
    queryKey: buildAssistantMessagesQueryKey(conversationId === null ? '' : conversationId),
    queryFn: function loadMessages({ pageParam }) {
      return fetchAssistantMessages(conversationId === null ? '' : conversationId, pageParam);
    },
    initialPageParam: null as number | null,
    getNextPageParam: readOlderPageCursor,
    enabled: conversationId !== null,
  });
}

export function flattenAssistantMessagePages(
  data: InfiniteData<AssistantMessagePage, number | null> | undefined
): AssistantMessage[] {
  if (data === undefined) {
    return [];
  }
  return [...data.pages].reverse().flatMap(function readItems(page) {
    return page.items;
  });
}

export function useDeleteAssistantConversationMutation(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAssistantConversation,
    onSuccess: function refreshConversations() {
      void queryClient.invalidateQueries({ queryKey: ASSISTANT_CONVERSATIONS_QUERY_KEY });
    },
  });
}

interface AssistantFeedbackVariables {
  messageId: string;
  feedback: AssistantFeedback | null;
  reason: AssistantFeedbackReason | null;
}

export function useAssistantFeedbackMutation(): UseMutationResult<
  AssistantMessage,
  unknown,
  AssistantFeedbackVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function sendFeedback(variables: AssistantFeedbackVariables) {
      return setAssistantFeedback(variables.messageId, variables.feedback, variables.reason);
    },
    onSuccess: function refreshMessages() {
      void queryClient.invalidateQueries({ queryKey: ASSISTANT_MESSAGES_QUERY_KEY });
    },
  });
}
